// Phase 5F — minimal customer-visible panel for Scheduling AI
// (Thoro-powered). Hidden when PHASE5F_SCHEDULING_AI_ENABLED is off
// (catalogue returns 503). Reuses the human-authority proposal /
// confirmation UX pattern.
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  Sparkles, Loader2, X, Send, ShieldCheck, Check, AlertTriangle,
} from "lucide-react";
import {
  fetchSchedulingAiCatalogue, schedulingAiNl,
  schedulingAiConfirm, schedulingAiCancel,
} from "@/lib/schedulingAi";

export default function SchedulingAiPanel({ onAfterMutation }) {
  const [enabled, setEnabled] = useState(null); // null = unknown
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchSchedulingAiCatalogue()
      .then((r) => setEnabled(!!r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, open]);

  if (enabled !== true) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setTurns((t) => [...t, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await schedulingAiNl(text);
      setTurns((t) => [...t, { role: "ai", kind: r.kind, payload: r }]);
    } catch (e) {
      setTurns((t) => [...t, {
        role: "ai", kind: "error",
        payload: { message: e?.response?.data?.detail?.message
          || e?.response?.data?.detail?.code || e.message },
      }]);
    } finally { setBusy(false); }
  };

  const confirm = async (idx, proposal) => {
    setBusy(true);
    try {
      const r = await schedulingAiConfirm(
        proposal.action_row_id, proposal.confirmation_token);
      setTurns((t) => {
        const n = [...t]; n[idx] = { ...n[idx], confirmed: true, result: r };
        return n;
      });
      toast.success("Done. The schedule may need revalidation.");
      onAfterMutation?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail?.code || e.message);
    } finally { setBusy(false); }
  };

  const cancel = async (idx, proposal) => {
    setBusy(true);
    try {
      await schedulingAiCancel(proposal.action_row_id);
      setTurns((t) => {
        const n = [...t]; n[idx] = { ...n[idx], cancelled: true };
        return n;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="scheduling-ai-open-btn"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#202B60] text-white text-[13px] font-medium shadow-lg hover:bg-[#182149]"
        title="Ask Thoro about scheduling"
      >
        <Sparkles size={14} /> Ask Thoro
      </button>
      {open && (
        <div
          className="fixed inset-y-0 right-0 w-full sm:w-[420px] z-50 bg-white border-l border-[#eee] shadow-2xl flex flex-col"
          data-testid="scheduling-ai-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#eee]">
            <div className="inline-flex items-center gap-2">
              <Sparkles size={16} className="text-[#202B60]" />
              <div>
                <div className="text-[13px] font-semibold">Thoro — Scheduling</div>
                <div className="text-[11px] text-[#777]">AI drafts suggestions; you keep the authority.</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="scheduling-ai-close-btn">
              <X size={16} />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="scheduling-ai-transcript">
            {turns.length === 0 && (
              <div className="text-[12px] text-[#777] italic">
                Try: <i>"Why can't I publish Week 46?"</i> or <i>"Revalidate the current draft"</i> or <i>"Show my schedules"</i>.
              </div>
            )}
            {turns.map((t, i) => (
              <TurnBubble
                key={i} turn={t} index={i}
                onConfirm={confirm} onCancel={cancel}
                busy={busy}
              />
            ))}
          </div>
          <div className="border-t border-[#eee] p-3">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask Thoro about scheduling…"
                disabled={busy}
                className="zy-input text-[13px] flex-1"
                data-testid="scheduling-ai-input"
              />
              <button
                onClick={send} disabled={busy || !input.trim()}
                data-testid="scheduling-ai-send-btn"
                className="px-3 py-1.5 rounded-md bg-[#202B60] text-white text-[13px] disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            <div className="text-[11px] text-[#999] mt-2 inline-flex items-center gap-1">
              <ShieldCheck size={11} /> Publishing is always your decision — Thoro never publishes rosters.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TurnBubble({ turn, index, onConfirm, onCancel, busy }) {
  if (turn.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-[#202B60] text-white text-[13px] px-3 py-2 rounded-2xl rounded-br-sm max-w-[80%]" data-testid={`scheduling-ai-user-${index}`}>
          {turn.text}
        </div>
      </div>
    );
  }
  const p = turn.payload || {};
  const common = "bg-[#F4F6FB] text-[13px] px-3 py-2 rounded-2xl rounded-bl-sm max-w-[90%]";
  if (turn.kind === "prose" || turn.kind === "clarification") {
    return (
      <div className="flex justify-start">
        <div className={common} data-testid={`scheduling-ai-ai-${index}`}>
          {p.message}
        </div>
      </div>
    );
  }
  if (turn.kind === "error") {
    return (
      <div className="flex justify-start">
        <div className="bg-[rgba(220,38,38,0.08)] text-[#b91c1c] text-[13px] px-3 py-2 rounded-2xl rounded-bl-sm max-w-[90%] inline-flex items-start gap-2" data-testid={`scheduling-ai-ai-${index}`}>
          <AlertTriangle size={14} className="mt-0.5" /> {p.message || "Something went wrong."}
        </div>
      </div>
    );
  }
  if (turn.kind === "read_result") {
    const r = p.result || {};
    return (
      <div className="flex justify-start w-full">
        <div className="bg-white border border-[#eee] text-[13px] px-3 py-2 rounded-2xl rounded-bl-sm max-w-[90%] w-full" data-testid={`scheduling-ai-read-${index}`}>
          {r.schedules && (
            <div>
              <div className="font-semibold mb-1">Your schedules</div>
              <ul className="space-y-1 text-[12px]">
                {r.schedules.map((s, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span>{s.name} <span className="text-[#777]">· {s.status}{s.version?` · v${s.version}`:""}</span></span>
                    <span className="text-[11px] text-[#777]">{s.period_start} → {s.period_end}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {r.schedule && (
            <div>
              <div className="font-semibold">{r.schedule.name}</div>
              <div className="text-[12px] text-[#555]">
                {r.schedule.status}{r.schedule.version?` · v${r.schedule.version}`:""} · {r.schedule.period_start} → {r.schedule.period_end}
              </div>
              <div className="text-[12px] mt-1">
                <b>{r.assignment_count}</b> assignment{r.assignment_count===1?"":"s"} ·
                <span className="text-[#b91c1c]"> {r.error_count} error{r.error_count===1?"":"s"}</span> ·
                <span className="text-[#b45309]"> {r.warning_count} warning{r.warning_count===1?"":"s"}</span>
              </div>
              {r.publish_blocked ? (
                <div className="text-[12px] text-[#b91c1c] mt-1">Publishing is blocked — resolve the issues in the Scheduling page.</div>
              ) : (
                <div className="text-[12px] text-[#059669] mt-1">Ready for you to publish from the Scheduling page.</div>
              )}
              {(r.assignments || []).slice(0, 5).map((a, i) => (
                <div key={i} className="text-[12px] mt-1">
                  • {a.employee_display_name} · {a.date} · {a.window_start}–{a.window_end}
                </div>
              ))}
              {(r.assignments?.length || 0) > 5 && (
                <div className="text-[11px] text-[#777] mt-1">…and {r.assignments.length - 5} more.</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (turn.kind === "proposal") {
    const prop = p.proposal || {};
    const disp = prop.display || {};
    return (
      <div className="flex justify-start w-full">
        <div className="bg-white border border-[#202B60] text-[13px] px-3 py-3 rounded-2xl rounded-bl-sm max-w-[90%] w-full space-y-2" data-testid={`scheduling-ai-proposal-${index}`}>
          <div className="font-semibold">{prop.description || "Confirm this action?"}</div>
          {disp.assignment && (
            <div className="text-[12px] bg-[#F4F6FB] rounded p-2">
              <b>{disp.assignment.employee_display_name}</b> · {disp.assignment.date}<br/>
              <span className="text-[#777]">{disp.assignment.window_start}–{disp.assignment.window_end}</span>
              <span className="mx-2">→</span>
              <b>{disp.new_window_start}–{disp.new_window_end}</b>
              {disp.schedule_name && <div className="text-[11px] text-[#777] mt-1">Schedule: {disp.schedule_name}</div>}
            </div>
          )}
          {disp.schedule && (
            <div className="text-[12px] bg-[#F4F6FB] rounded p-2">
              <b>{disp.schedule.name}</b> ({disp.schedule.status}{disp.schedule.version?` v${disp.schedule.version}`:""})
              <div className="text-[11px] text-[#777]">{disp.schedule.period_start} → {disp.schedule.period_end}</div>
            </div>
          )}
          {!turn.confirmed && !turn.cancelled && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onConfirm(index, prop)} disabled={busy}
                data-testid={`scheduling-ai-confirm-${index}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#202B60] text-white text-[12px]"
              >
                <Check size={12} /> Confirm
              </button>
              <button
                onClick={() => onCancel(index, prop)} disabled={busy}
                data-testid={`scheduling-ai-cancel-${index}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#ddd] text-[12px]"
              >
                <X size={12} /> Cancel
              </button>
            </div>
          )}
          {turn.confirmed && (
            <div className="text-[12px] text-[#059669] inline-flex items-center gap-1">
              <Check size={13} /> Done. {turn.result?.result?.needs_revalidation ? "Remember to revalidate." : ""}
            </div>
          )}
          {turn.cancelled && (
            <div className="text-[12px] text-[#777]">Cancelled.</div>
          )}
        </div>
      </div>
    );
  }
  return null;
}
